import type { Markup } from '@/types';
import type { ReviewDefaultedField } from '@/types/commands';
import type { Media } from '@/types/schemas';
import { applyMediaPatch, type MediaPatch } from './mediaPatch';

/**
 * Markup revision + review-loop defaults.
 *
 * `rev` is the integer optimistic-concurrency token of the markup review loop
 * (bim-pdf 1.11 Annotation.rev). It starts at 1 and every write through a store
 * adapter (useMarkups / useLocalMarkups / LocalWorkspaceService) bumps it by
 * exactly 1 via `applyMarkupWrite`, so every MarkupCommand the interpreter
 * executes -- plus the resolver side-writes and undo/redo, which reach the same
 * adapters -- advances it. See docs/decisions/2026-09-22-markup-rev-and-review-role.md.
 *
 * `updatedAt` keeps its own monotonic bump (nextMarkupUpdatedAt) for the undo
 * manager's "changed later?" check; it is no longer the agent revision.
 */

/** Rev of a markup; pre-1.11 data without a rev reads as 1. */
export function getMarkupRev(markup: Partial<Pick<Markup, 'rev'>>): number {
  const rev = markup.rev;
  return typeof rev === 'number' && Number.isInteger(rev) && rev >= 1 ? rev : 1;
}

/** The rev the next write of `markup` must carry (current + 1). */
export function nextMarkupRev(markup: Partial<Pick<Markup, 'rev'>> | undefined): number {
  return markup ? getMarkupRev(markup) + 1 : 1;
}

export function nextMarkupUpdatedAt(previousUpdatedAt?: Date, now = new Date()): Date {
  if (!previousUpdatedAt) {
    return now;
  }

  return now.getTime() > previousUpdatedAt.getTime()
    ? now
    : new Date(previousUpdatedAt.getTime() + 1);
}

/**
 * A write carried an `expectedRev` that no longer matches the markup's current
 * rev (someone else wrote in between), or the markup is gone. HTTP-409 shaped
 * so the agent surface and the future Firestore-rules denial read the same.
 */
export class RevisionConflictError extends Error {
  readonly code = 409 as const;
  constructor(
    readonly markupId: string,
    readonly expectedRev: number,
    /** Current rev, or null when the markup no longer exists. */
    readonly actualRev: number | null,
  ) {
    super(
      actualRev === null
        ? `rev conflict on markup ${markupId}: expected rev ${expectedRev}, markup no longer exists`
        : `rev conflict on markup ${markupId}: expected rev ${expectedRev}, current rev ${actualRev}`,
    );
    this.name = 'RevisionConflictError';
  }
}

/**
 * Review STATE: the markup fields no client writes, on any project (R6-4) --
 * firestore.rules (frozenInkFields + requestInkStateFields) and the local guard
 * refuse every one of them. The review state (`status`, `status_history`: a
 * create carries only the fresh one, withReviewDefaults), the request service's
 * field and what the request view projects onto a gated project's ink in memory
 * (core REQUEST_CONTRACT.clientWrites.inkFields: `request_id`, the R2 lock
 * `dispatch_id` + `released`, `trace_id`), and the frozen fields the deleted v8
 * loop left on markups at rest (core LEGACY_REVIEW_FIELDS). A v8 markup edits as
 * plain ink; a restore / import / patch never carries one of these.
 * revision.test.ts pins this list to those three sources.
 */
export const REVIEW_STATE_FIELDS = [
  'status', 'status_history',
  'request_id', 'dispatch_id', 'released', 'trace_id',
  'request', 'review', 'delta', 'contractVersion', 'in_reply_to', 'addresses', 'sheet_anchor', 'agent_status',
  'needs_human_reason', 'proposal_markup_id', 'proposal_expires_at', 'apply', 'resolved_package_hash',
] as const;
export type ReviewStateField = (typeof REVIEW_STATE_FIELDS)[number];

/** `value` without any review-state field (see REVIEW_STATE_FIELDS). */
export function withoutReviewState<T extends object>(value: T): Omit<T, ReviewStateField> {
  const copy: Record<string, unknown> = { ...(value as Record<string, unknown>) };
  for (const field of REVIEW_STATE_FIELDS) delete copy[field];
  return copy as Omit<T, ReviewStateField>;
}

/**
 * The create-time review state on a draft: rev (the draft's, else 1), status
 * open, empty history, and no other REVIEW_STATE_FIELDS. Whatever review state the
 * draft carries is DISCARDED, not defaulted: a create (placeMarkup, an
 * annotations.json import, a restore) always starts the walk at `open`, in
 * every store, so review state cannot be forged locally and a cloud create is
 * never half-denied by the rules. See architecture review 2026-09-23, 0.5.
 */
export function withReviewDefaults<T extends object & Partial<Pick<Markup, 'rev'>>>(
  draft: T,
): Omit<T, ReviewStateField> & Pick<Markup, ReviewDefaultedField> {
  return {
    ...withoutReviewState(draft),
    rev: draft.rev ?? 1,
    status: 'open',
    status_history: [],
  };
}

/**
 * The bookkeeping fields every markup write must carry: `rev` = existing + 1
 * and a monotonic `updatedAt`. Any `rev` in the caller's patch is ignored --
 * the rev is derived from the stored markup, never supplied by the caller, so
 * an undo that replays an old `rev` cannot move it backwards.
 */
export function markupWriteStamp(
  existing: Pick<Markup, 'updatedAt'> & Partial<Pick<Markup, 'rev'>> | undefined,
  now = new Date(),
): Pick<Markup, 'rev' | 'updatedAt'> {
  return {
    rev: nextMarkupRev(existing),
    updatedAt: nextMarkupUpdatedAt(existing?.updatedAt, now),
  };
}

/**
 * The CLOUD (Firestore) form of a markup write: the patch plus a monotonic
 * `updatedAt`, with any caller `rev` DROPPED. The cloud rev is not computed on
 * the client at all: `FirestoreService.updateMarkup` / `batchUpdateMarkups`
 * write `rev: increment(1)`, which the server applies to the stored value, so
 * the rules' `rev == old rev + 1` holds by construction however stale the
 * client's snapshot is (two writes of one drag land before the listener sees
 * the first). Local stores keep `markupWriteStamp`, which is exact because they
 * read the stored markup synchronously.
 */
export function cloudMarkupPatch(
  existing: Pick<Markup, 'updatedAt'> | undefined,
  updates: Partial<Markup>,
  now = new Date(),
): Partial<Markup> {
  const { rev: _ignoredRev, ...patch } = updates;
  void _ignoredRev;
  return { ...patch, updatedAt: nextMarkupUpdatedAt(existing?.updatedAt, now) };
}

/**
 * Fields an UPDATE removes when it carries null (a photo pin's room: cleared, or moved out of every room -- image
 * markup spec 4.5). The remote backends write their deleteField (services/markupFields.ts withClearedFields);
 * applyMarkupWrite drops the key. The stored shape never holds null.
 */
export const NULL_CLEARED_FIELDS = ['room'] as const;

/**
 * Merge `updates` onto `existing` as one write: patch, then rev + 1 and updatedAt. An update's `media` is a per-item
 * patch (core/markups/mediaPatch.ts), merged item by item as the remote backends' dotted paths are -- never the
 * whole map.
 */
export function applyMarkupWrite(existing: Markup, updates: Partial<Markup>, now = new Date()): Markup {
  const out: Record<string, unknown> = { ...existing, ...updates, ...markupWriteStamp(existing, now) };
  for (const field of NULL_CLEARED_FIELDS) if (out[field] === null) delete out[field];
  if ('media' in updates) {
    const media = applyMediaPatch((existing as { media?: Media }).media, ((updates as { media?: MediaPatch }).media ?? {}));
    if (media) out.media = media;
    else delete out.media;
  }
  return out as Markup;
}

/**
 * The markup a RESTORE writes (undo of a delete, redo of a create, import by
 * id): the snapshot with `rev` = max(snapshot rev, lastKnownRev) + 1, so a
 * markup that comes back never reuses a rev it (or its previous life) already
 * had -- an agent holding a pre-delete rev gets a 409, not a silent overwrite.
 * `lastKnownRev` is the rev the markup had when it was last deleted (the undo
 * manager tracks it), which can be newer than the snapshot being restored.
 *
 * A restore is a CREATE, so the review state restarts (status open, empty
 * history, no delta / in_reply_to): the rules deny any other review state on a
 * client create, and locally it would re-create state only the review path may
 * write. Undoing the delete of a markup that was mid-review therefore brings it
 * back as an open markup.
 *
 * `updatedAt` is deliberately KEPT from the snapshot: the undo manager's
 * "modified since this step?" check compares it against older steps, and a
 * fresh timestamp would make every chained undo after a restore conflict.
 */
export function restoredMarkup(markup: Markup, lastKnownRev?: number): Markup {
  const base = Math.max(getMarkupRev(markup), lastKnownRev ?? 0);
  // A restore is a create: the review state restarts at open (withReviewDefaults).
  return { ...withReviewDefaults(markup), rev: base + 1 } as Markup;
}
